import React, { useEffect, useState } from "react";
import { Upload, Download, Trash2, AlertTriangle, FileText, Plus, X, Settings } from "lucide-react";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const DOC_TYPES = [
  "Offer Letter", "Employment Contract", "NDA", "Salary Certificate",
  "Experience Letter", "Payslip", "ID Proof", "Address Proof",
  "Educational Certificate", "Background Check", "Form 16", "Other",
];

const GENERATE_TYPES = [
  { key: "offer_letter",        label: "Offer Letter",        available: true  },
  { key: "experience_letter",   label: "Experience Letter",   available: true  },
  { key: "salary_certificate",  label: "Salary Certificate",  available: false },
  { key: "noc",                 label: "NOC Letter",          available: true  },
  { key: "increment_letter",    label: "Increment Letter",    available: false },
];

// Experience letter reason picker — passed through to the backend so
// it can render the "This certificate is issued for the purpose of ..."
// line correctly.
const EXPERIENCE_REASONS = [
  { key: "bank_loan",               label: "Bank loan application"     },
  { key: "visa_immigration",        label: "Visa / immigration"        },
  { key: "employment_verification", label: "General employment verify" },
  { key: "address_rental",          label: "Address / rental verify"   },
  { key: "other",                   label: "Other (free text)"         },
];

function expiryBadge(date) {
  if (!date) return null;
  const days = dayjs(date).diff(dayjs(), "day");
  if (days < 0)   return <span className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">Expired</span>;
  if (days <= 30) return <span className="text-xs text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-full">Expires in {days}d</span>;
  if (days <= 90) return <span className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 rounded-full">{dayjs(date).format("DD MMM YYYY")}</span>;
  return <span className="text-xs text-white/25">{dayjs(date).format("DD MMM YYYY")}</span>;
}

export default function AdminDocuments({ userId, userName }) {
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ type: "", expiry: "", file: null });
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [genMsg,    setGenMsg]    = useState(null);
  const [search,    setSearch]    = useState("");
  const [expModal,  setExpModal]  = useState(false);
  const [expReason, setExpReason] = useState("bank_loan");
  const [expNote,   setExpNote]   = useState("");
  const [templatesModal, setTemplatesModal] = useState(false);

  const token = localStorage.getItem("token");

  const load = async () => {
    if (!userId) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : data?.documents || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId]);

  const handleUpload = async () => {
    if (!uploadForm.file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("type", uploadForm.type);
      if (uploadForm.expiry) fd.append("expiry_date", uploadForm.expiry);
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowUpload(false);
      setUploadForm({ type: "", expiry: "", file: null });
      await load();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const handleDelete = async (docId) => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/documents/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch (e) { setError(e.message); }
  };

  // Kick a PDF download for one letter. Backend streams application/pdf
  // (matching LettersTab.jsx's proven Blob-download pattern), not JSON.
  // Experience needs `reason` (+ optional `reason_note` when reason='other'),
  // provided by the small modal.
  const generate = async (type, extra = {}) => {
    if (!userId) return;
    setGenerating(type); setGenMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/generate-document`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ type, ...extra }),
      });
      if (!res.ok) {
        // Backend surfaces "not available yet" for deferred types and
        // "no organisation" cases as JSON detail; try to read it.
        let detail = `HTTP ${res.status}`;
        try {
          const err = await res.json();
          if (err?.detail) detail = typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
        } catch { /* not JSON */ }
        throw new Error(detail);
      }
      const label = GENERATE_TYPES.find(t => t.key === type)?.label || "Document";
      const safe  = (userName || "employee").replace(/[^\w\s.-]/g, "").trim();
      const blob  = await res.blob();
      const url   = URL.createObjectURL(blob);
      const a     = document.createElement("a");
      a.href      = url;
      a.download  = `${label} - ${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setGenMsg(`✓ ${label} generated`);
      // Backend now archives the letter into employee_documents on the
      // same request — refresh the list so it appears without a reload.
      load();
    } catch (e) { setGenMsg(`⚠ ${e.message}`); }
    finally { setGenerating(null); }
  };

  const handleGenerate = (type) => {
    if (!userId) return;
    if (type === "experience_letter") {
      setExpReason("bank_loan");
      setExpNote("");
      setExpModal(true);
      return;
    }
    generate(type);
  };

  const submitExperience = () => {
    const extra = { reason: expReason };
    if (expReason === "other") {
      const note = expNote.trim();
      if (!note) { setGenMsg("⚠ Please enter a reason for the Experience letter."); return; }
      extra.reason_note = note;
    }
    setExpModal(false);
    generate("experience_letter", extra);
  };

  // Expiry alerts
  const expiring = docs.filter(d => {
    if (!d.expiry_date) return false;
    return dayjs(d.expiry_date).diff(dayjs(), "day") <= 30;
  });

  const filtered = docs.filter(d =>
    !search || (d.type || d.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">

      {/* Expiry alerts */}
      {expiring.length > 0 && (
        <div className="rounded-xl border border-orange-500/25 bg-orange-500/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-orange-400" />
            <p className="text-xs text-orange-400 font-medium uppercase tracking-wider">{expiring.length} document{expiring.length !== 1 ? "s" : ""} expiring soon</p>
          </div>
          <div className="space-y-1">
            {expiring.map(d => (
              <p key={d.id} className="text-xs text-orange-400/70">
                {d.type || d.name} · expires {dayjs(d.expiry_date).format("DD MMM YYYY")}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Generate documents */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Generate Documents</span>
          <button
            onClick={() => setTemplatesModal(true)}
            title="Manage letter templates"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/5 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Settings size={12} /> Templates
          </button>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {GENERATE_TYPES.map(({ key, label, available }) => {
            const disabled = generating !== null || !userId || !available;
            const title = !userId
              ? "Select a user first"
              : !available
              ? "Not available yet — coming in the next build"
              : `Generate ${label}`;
            return (
              <button key={key} onClick={() => handleGenerate(key)} disabled={disabled} title={title}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${
                  available
                    ? "border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40"
                    : "border-white/10 bg-white/5 text-white/30 cursor-not-allowed"
                }`}>
                <FileText size={12} />
                {generating === key ? "Generating…" : label}
                {!available && <span className="text-[10px] text-white/25 ml-1">(soon)</span>}
              </button>
            );
          })}
        </div>
        {genMsg && <p className={`px-5 pb-3 text-xs ${genMsg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{genMsg}</p>}
      </div>

      {/* Templates — placeholder for the upcoming builder */}
      {templatesModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="w-full max-w-md rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Settings size={14} className="text-[#00ff90]/70" /> Manage Templates
              </h3>
              <button onClick={() => setTemplatesModal(false)} className="text-white/30 hover:text-white transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="rounded-lg border border-[#00ff90]/15 bg-[#00ff90]/5 px-4 py-3">
              <p className="text-xs text-[#00ff90]/80 uppercase tracking-wider font-medium mb-1">Coming next</p>
              <p className="text-xs text-white/60 leading-relaxed">
                A template builder — upload your own letterhead, edit the seeded Offer / Experience / NOC bodies, and add
                custom templates per org — is shipping in the next build. This button is the entry point; the editor
                itself is a separate task.
              </p>
            </div>
            <p className="text-[11px] text-white/30 leading-relaxed">
              For now the three built-in templates (Standard Offer Letter, Experience Certificate, NOC Letter) are used
              as-is. Salary Certificate and Increment Letter arrive with the compensation-modal build.
            </p>
            <div className="flex justify-end pt-1">
              <button onClick={() => setTemplatesModal(false)}
                className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Experience letter — reason picker */}
      {expModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="w-full max-w-md rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022] p-6 space-y-4">
            <h3 className="text-sm font-semibold text-white">Experience Letter — reason</h3>
            <p className="text-xs text-white/40">Pick the purpose. This appears as one line in the letter (e.g. "issued for the purpose of a bank loan application").</p>

            <div>
              <label className={labelCls}>Reason</label>
              <select className={inputCls} value={expReason} onChange={e => setExpReason(e.target.value)}>
                {EXPERIENCE_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>

            {expReason === "other" && (
              <div>
                <label className={labelCls}>Custom reason</label>
                <input className={inputCls} value={expNote} onChange={e => setExpNote(e.target.value)}
                  placeholder="e.g. securing a school admission" maxLength={200} />
                <p className="text-[10px] text-white/25 mt-1">Renders as: "issued for the purpose of &lt;your text&gt;."</p>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setExpModal(false)}
                className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={submitExperience}
                className="text-xs px-5 py-2 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/15 text-[#00ff90] hover:bg-[#00ff90]/25 transition-colors font-medium">
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Documents</span>
          <div className="flex items-center gap-2">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search…" className="bg-black border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none w-32" />
            <button onClick={() => setShowUpload(v => !v)}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              <Plus size={12} /> Upload
            </button>
          </div>
        </div>

        {/* Upload form */}
        {showUpload && (
          <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider">Upload Document</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Document Type</label>
                <select className={inputCls} value={uploadForm.type} onChange={e => setUploadForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="">Select type…</option>
                  {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Expiry Date (optional)</label>
                <input type="date" className={inputCls} value={uploadForm.expiry}
                  onChange={e => setUploadForm(f => ({ ...f, expiry: e.target.value }))} />
              </div>
            </div>
            <div className="border border-dashed border-[#00ff90]/20 rounded-lg px-4 py-3 bg-[#00ff90]/5 flex items-center gap-3">
              <Upload size={14} className="text-[#00ff90]/40 shrink-0" />
              <input type="file" onChange={e => setUploadForm(f => ({ ...f, file: e.target.files[0] }))}
                className="text-xs text-white/50 w-full" />
            </div>
            {uploadForm.file && <p className="text-xs text-white/30">📎 {uploadForm.file.name}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowUpload(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={handleUpload} disabled={uploading || !uploadForm.file}
                className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        )}

        {error && <p className="px-5 py-3 text-red-400 text-sm">⚠ {error}</p>}

        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="px-5 py-4"><div className="h-10 rounded-lg bg-white/5 animate-pulse" /></div>
            ))
          ) : filtered.length === 0 ? (
            <p className="px-5 py-8 text-center text-white/20 text-sm italic">No documents uploaded yet.</p>
          ) : (
            filtered.map(doc => (
              <div key={doc.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={14} className="text-[#00ff90]/40 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-white/70 truncate">{doc.type || doc.name || "Document"}</p>
                    <p className="text-xs text-white/25 mt-0.5">
                      {doc.uploaded_by && `Uploaded by ${doc.uploaded_by} · `}
                      {doc.uploaded_at && dayjs(doc.uploaded_at).format("DD MMM YYYY")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {expiryBadge(doc.expiry_date)}
                  {doc.url && (
                    <a href={doc.url} target="_blank" rel="noreferrer"
                      className="text-[#00ff90]/50 hover:text-[#00ff90] transition-colors">
                      <Download size={14} />
                    </a>
                  )}
                  <button onClick={() => handleDelete(doc.id)} className="text-white/20 hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}